import { useState, useEffect } from "react";

import styled from "styled-components";
import { DeleteOutlined } from "@ant-design/icons";
import { message, Modal, Input, Popconfirm } from "antd";

import { Annotation, deleteAnnotation  } from "../../api/slideAPI";
import { checkUserName } from "../../api/authAPI";
import { hasTempViewerSession } from "../../api/api";
import { ensureTempViewerPassword, getStoredTempViewerName } from "../../lib/tempViewer";

const ADMIN_ROLES = ["ADMIN_MAIN", "ADMIN_SUB"]; // 

interface Props {
    annoList: Array<Annotation>;
    reloadAnnoList: () => void;
    isSharedView?: boolean;
    userRole?: string;
}

const AnnotationArea = ({ annoList, reloadAnnoList, isSharedView = false, userRole = "" }: Props) => {
  const isAdmin = ADMIN_ROLES.includes(userRole);
  const [userName, setUserName] = useState<string | null>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [pendingAnnoId, setPendingAnnoId] = useState<number | null>(null);
  const [passwordInput, setPasswordInput] = useState("");
  const isTempViewer = hasTempViewerSession();

  const handleDeleteAnno = async (annoId: number) => {
    if (isSharedView) {
      setPendingAnnoId(annoId);
      setPasswordInput("");
      setPasswordModalOpen(true);
      return;
    }

    const anonymousPassword = isTempViewer ? ensureTempViewerPassword() : null;
    if (isTempViewer && !anonymousPassword) return;

    const result = await deleteAnnotation(annoId, anonymousPassword ?? undefined);
    if (!result) {
      message.error("어노테이션 삭제에 실패했습니다.");
      return;
    }

    message.success("어노테이션이 삭제되었습니다.");
    reloadAnnoList();
  };

  const handlePasswordConfirm = async () => {
    if (!pendingAnnoId) return;
    if (!passwordInput.trim()) {
      message.warning("비밀번호를 입력해주세요.");
      return;
    }

    const result = await deleteAnnotation(pendingAnnoId, passwordInput);
    if (!result) {
      message.error("비밀번호가 올바르지 않거나 삭제에 실패했습니다.");
      return;
    }

    message.success("어노테이션이 삭제되었습니다.");
    setPasswordModalOpen(false);
    setPendingAnnoId(null);
    setPasswordInput("");
    reloadAnnoList();
  };

  useEffect(() => {
    if (isTempViewer) {
      setUserName(getStoredTempViewerName());
      return;
    }

    checkUserName().then((name) => setUserName(name));
  }, [annoList, isTempViewer]);

  return (
    <>
    <StAnnoListAreaContainer>
      <h3 style={{ margin: 0 }}>Annotations</h3>
      {annoList?.map((anno) => (
        <StAnnoItem key={anno?.annoId}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: "16px" }}>
            {anno?.content}
            {anno?.anonymous && (
              <StTempUserLabel>(링크 접근 사용자)</StTempUserLabel>
            )}
          </div>
          {isSharedView
            ? userName === anno.writer && anno.anonymous && (
                <DeleteOutlined
                  style={{ color: "#ff4d4f", cursor: "pointer", fontSize: "16px" }}
                  onClick={() => handleDeleteAnno(anno.annoId)}
                />
              )
            : (anno.anonymous ? isAdmin : userName === anno.writer) && (
                <Popconfirm
                  title="삭제하시겠습니까?"
                  onConfirm={() => handleDeleteAnno(anno.annoId)}
                  okText="삭제"
                  cancelText="취소"
                >
                  <DeleteOutlined style={{ color: "#ff4d4f", cursor: "pointer", fontSize: "16px" }} />
                </Popconfirm>
              )
          }
        </div>
      </StAnnoItem>
      ))}
    </StAnnoListAreaContainer>
    <Modal
      title="비밀번호 확인"
      open={passwordModalOpen}
      onOk={handlePasswordConfirm}
      onCancel={() => { setPasswordModalOpen(false); setPendingAnnoId(null); setPasswordInput(""); }}
      okText="확인"
      cancelText="취소"
      centered
    >
      <Input.Password
        placeholder="등록 시 사용한 비밀번호를 입력하세요"
        value={passwordInput}
        onChange={(e) => setPasswordInput(e.target.value)}
        onPressEnter={handlePasswordConfirm}
      />
    </Modal>
    </>
  );
}

const StAnnoListAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
`;

const StAnnoItem = styled.div`
  padding: 0.5rem;
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.7);
`;

const StTempUserLabel = styled.span`
  margin-left: 0.375rem;
  font-size: 0.6875rem;
  color: #aaa;
  font-weight: normal
`
export default AnnotationArea;
